import claraPeek from "@/assets/assistants/clara-peek-aligned.png";
import rosaPeek from "@/assets/assistants/rosa-peek-aligned.png";
import hugoPeek from "@/assets/assistants/hugo-peek-aligned.png";
import albaPeek from "@/assets/assistants/alba-peek-skirt.png";
import sofiaPeek from "@/assets/assistants/sofia-peek-elegant.png";

const REFINED_PEEK: Record<string, string> = { clara: claraPeek, rosa: rosaPeek, hugo: hugoPeek, alba: albaPeek, sofia: sofiaPeek };
export const assistantPeekAlignment = (id: string) => id === "hugo" ? "translate-y-[8%]" : "";

export type AssistantGender = "male" | "female";
export interface AssistantDef { id: string; name: string; gender: AssistantGender; es: string; en: string; img: string }

// Cambia este valor cuando se sustituya algún dibujo: los navegadores guardan
// las imágenes durante un año, así que sin esta marca no vuelven a pedir la nueva.
const ASSISTANT_ASSET_VERSION = "2026-10-18a";

const a = (id: string, name: string, gender: AssistantGender, es: string, en: string, _x?: unknown): AssistantDef =>
  ({ id, name, gender, es, en, img: `/assistants/${id}.webp?v=${ASSISTANT_ASSET_VERSION}` });

export const ASSISTANTS: AssistantDef[] = [
  a("leo", "Leo", "male", "Joven y directo", "Young and direct"),
  a("diego", "Diego", "male", "Formal y claro", "Formal and clear"),
  a("marcos", "Marcos", "male", "Experimentado y calmado", "Experienced and calm"),
  a("nico", "Nico", "male", "Cercano y práctico", "Friendly and practical"),
  a("andres", "Andrés", "male", "Analítico y preciso", "Analytical and precise"),
  a("hugo", "Hugo", "male", "Fresco y optimista", "Fresh and upbeat"),
  a("carlos", "Carlos", "male", "Sabio y cercano", "Wise and warm"),
  a("mateo", "Mateo", "male", "Relajado y cercano", "Relaxed and friendly"),
  a("alba", "Alba", "female", "Segura y directa", "Confident and direct"),
  a("luna", "Luna", "female", "Alternativa y creativa", "Alternative and creative"),
  a("mia", "Mia", "female", "Fresca y cercana", "Fresh and friendly"),
  a("elena", "Elena", "female", "Analítica y clara", "Analytical and clear"),
  a("sofia", "Sofía", "female", "Elegante y motivadora", "Elegant and motivating"),
  a("valeria", "Valeria", "female", "Práctica y resolutiva", "Practical and decisive"),
  a("clara", "Clara", "female", "Cálida y accesible", "Warm and approachable"),
  a("rosa", "Rosa", "female", "Experimentada y serena", "Experienced and serene"),
];

/** Valor guardado cuando el usuario prefiere no tener personaje dibujado. */
export const NO_ASSISTANT = "none";
export const getAssistant = (id?: string | null) => ASSISTANTS.find((x) => x.id === id) ?? null;
export const ASSISTANT_CHANGED_EVENT = "rl:assistant-changed";

/** Nombre por defecto del agente IA cuando el usuario aún no ha elegido asistente. */
export const AGENT_FALLBACK_NAME = "Sara";

/** Nombre base sin sufijo (uso interno). */
export const agentBaseName = (displayName?: string | null) => (displayName ?? "").trim() || AGENT_FALLBACK_NAME;

const isEnglishDoc = () =>
  typeof document !== "undefined" && (document.documentElement.lang || "").toLowerCase().startsWith("en");

/** Nombre visible del agente: siempre con «IA» (ES) o «AI» (EN), p. ej. «Sara IA» / «Sara AI». */
export const agentIaName = (displayName?: string | null, lang?: "es" | "en") => {
  const en = lang ? lang === "en" : isEnglishDoc();
  const base = agentBaseName(displayName).replace(/\s+(IA|AI)$/i, "");
  return `${base} ${en ? "AI" : "IA"}`;
};

/** Nombre visible del asistente (siempre con sufijo IA/AI). */
export const agentFirstName = (displayName?: string | null, lang?: "es" | "en") => agentIaName(displayName, lang);

/** Poses de cuerpo entero (en pruebas: solo algunos personajes las tienen). */
const ASSISTANT_POSES: Record<string, { lean?: boolean; sit?: boolean; peek?: boolean }> = {
  carlos: { lean: true, sit: true, peek: true },
  sofia: { lean: true, sit: true, peek: true },
  alba: { lean: true, sit: true, peek: true },
  clara: { lean: true, sit: true, peek: true },
  andres: { lean: true, sit: true, peek: true },
  marcos: { lean: true, sit: true, peek: true },
  rosa: { lean: true, sit: true, peek: true },
  elena: { lean: true, sit: true, peek: true },
  leo: { lean: true, sit: true, peek: true },
  hugo: { lean: true, sit: true, peek: true },
  diego: { lean: true, sit: true, peek: true },
  luna: { lean: true, sit: true, peek: true },
  mateo: { lean: true, sit: true, peek: true },
  mia: { lean: true, sit: true, peek: true },
  nico: { lean: true, sit: true, peek: true },
  valeria: { lean: true, sit: true, peek: true },
};
export function assistantPose(id: string | null | undefined, pose: "lean" | "sit" | "peek"): string | null {
  if (!id || !ASSISTANT_POSES[id]?.[pose]) return null;
  if (pose === "peek" && REFINED_PEEK[id]) return REFINED_PEEK[id];
  return `/assistants/poses/${id}-${pose}.webp?v=${ASSISTANT_ASSET_VERSION}`;
}

export type AssistantPoseKind = "peek" | "lean" | "sit";
/** Ruta de una pose aunque no esté activada (para el control del back office). */
export const assistantPoseFile = (id: string, pose: AssistantPoseKind) => pose === "peek" && REFINED_PEEK[id] ? REFINED_PEEK[id] : `/assistants/poses/${id}-${pose}.webp?v=${ASSISTANT_ASSET_VERSION}`;
export const assistantHasPose = (id: string, pose: AssistantPoseKind) => !!ASSISTANT_POSES[id]?.[pose];
/** Vista previa de administrador (solo esta pestaña; nunca se guarda en el perfil). */
export const ADMIN_ASSISTANT_PREVIEW_KEY = "rl:admin-assistant-preview";
